<template>
    <div class="node" :class="[isRoot ? 'is-root' : '', typeClass, inactiveClass]" role="treeitem"
        :aria-expanded="isLeaf ? undefined : !isCollapsed" :aria-labelledby="'label-' + node.id">
        <div class="node-box" :class="typeBoxClass">
            <button v-if="!isLeaf" type="button" class="caret" :class="{ open: !isCollapsed }" @click="toggle" />
            <span v-else class="caret placeholder" aria-hidden="true" />

            <button :id="'label-' + node.id" type="button" class="label" :title="node.label" @click="handleLabelClick">
                <i v-if="typeIcon" class="material-icons node-type-icon">{{ typeIcon }}</i>
                {{ node.label }}
            </button>

            <!-- Inactive badge -->
            <span v-if="isInactive" class="badge-inactive">Inactive</span>

            <!-- Instance-Workgroup indicators -->
            <span v-if="node.meta?.all_filled" class="badge-indicator badge-filled" title="All positions filled">
                <i class="material-icons">group</i>
            </span>
            <span v-if="node.meta?.all_confirmed" class="badge-indicator badge-confirmed" title="All users confirmed">
                <i class="material-icons">verified</i>
            </span>
        </div>

        <div v-if="!isLeaf && !isCollapsed" class="children" role="group" :id="'children-' + node.id">
            <TreeNodeItem v-for="child in node.children" :key="child.id" :node="child" :depth="depth + 1"
                @node-click="(data) => $emit('node-click', data)" />
        </div>
    </div>
</template>

<script setup>
import { defineProps, ref, computed } from 'vue'

const props = defineProps({
    node: { type: Object, required: true },
    depth: { type: Number, required: true },
    isRoot: { type: Boolean, default: false },
})

const emit = defineEmits(['node-click'])

const isCollapsed = ref(props.depth > 1)
const isLeaf = computed(() => !props.node.children || props.node.children.length === 0)
const nodeType = computed(() => props.node.type || '')
const isInactive = computed(() => props.node.status === 'inactive')

const typeClass = computed(() => nodeType.value ? `node-type-${nodeType.value}` : '')
const inactiveClass = computed(() => isInactive.value ? 'node-inactive' : '')
const typeBoxClass = computed(() => nodeType.value ? `box-${nodeType.value}` : '')

const typeIcon = computed(() => {
    const icons = {
        'workgroup': 'group_work',
        'instance': 'event',
        'instance-workgroup': 'assignment',
        'admin': 'admin_panel_settings',
        'preferred-user': 'star',
    }
    return icons[nodeType.value] || ''
})

const toggle = () => {
    if (isLeaf.value) return
    isCollapsed.value = !isCollapsed.value
}

const handleLabelClick = () => {
    if (nodeType.value === 'workgroup' || nodeType.value === 'instance-workgroup') {
        emit('node-click', { type: nodeType.value, node: props.node })
    } else {
        toggle()
    }
}
</script>

<style scoped>
.node { position: relative; display: inline-flex; flex-direction: row; align-items: center; gap: 18px; min-height: 36px; }

/* Root node */
.node.is-root > .node-box { background: linear-gradient(135deg, #4f46e5, #6366f1); border-color: #4338ca; color: #fff; box-shadow: 0 2px 8px rgba(79,70,229,0.25); }
.node.is-root > .node-box .label { color: #fff; font-weight: 600; }
.node.is-root > .node-box .label:hover { color: #e0e7ff; }
.node.is-root > .node-box .caret { background: rgba(255,255,255,0.2); }
.node.is-root > .node-box .caret::before { border-color: rgba(255,255,255,0.7); }
.node.is-root > .node-box .caret:hover { background: rgba(255,255,255,0.3); }

/* Node pill */
.node-box { display: inline-flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 5px 14px 5px 4px; white-space: nowrap; box-shadow: 0 1px 3px rgba(0,0,0,0.06); position: relative; transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s; }
.node-box:hover { border-color: #a5b4fc; box-shadow: 0 4px 12px rgba(99,102,241,0.15); transform: translateY(-1px); }

/* Type-based border colors */
.box-section { background: #f8f9fb; border-left: 3px solid #7c3aed; font-weight: 600; }
.box-workgroup { border-left: 3px solid #2563eb; }
.box-instance { border-left: 3px solid #7c3aed; }
.box-instance-workgroup { border-left: 3px solid #0d9488; }
.box-admin { border-left: 3px solid #d97706; }
.box-preferred-user { border-left: 3px solid #16a34a; }

/* Inactive */
.node-inactive > .node-box { opacity: 0.55; border-style: dashed; }
.node-inactive > .node-box .label { text-decoration: line-through; color: #94a3b8; }

/* Type icon */
.node-type-icon { font-size: 16px; vertical-align: middle; margin-right: 2px; }
.box-workgroup .node-type-icon { color: #2563eb; }
.box-instance .node-type-icon { color: #7c3aed; }
.box-instance-workgroup .node-type-icon { color: #0d9488; }
.box-admin .node-type-icon { color: #d97706; }
.box-preferred-user .node-type-icon { color: #16a34a; }

/* Badges */
.badge-inactive { font-size: 0.65rem; font-weight: 700; background: #fef2f2; color: #dc2626; padding: 1px 8px; border-radius: 10px; border: 1px solid #fecaca; margin-left: 4px; }
.badge-indicator { display: inline-flex; align-items: center; margin-left: 4px; }
.badge-indicator i { font-size: 14px; }
.badge-filled { color: #2563eb; }
.badge-confirmed { color: #16a34a; }

/* Caret */
.caret { --size: 20px; width: var(--size); height: var(--size); display: inline-flex; align-items: center; justify-content: center; border: none; background: #f1f5f9; border-radius: 4px; cursor: pointer; padding: 0; position: relative; transition: background 0.15s; }
.caret:hover { background: #e2e8f0; }
.caret::before { content: ''; width: 5px; height: 5px; border: 1.5px solid #64748b; border-left: 0; border-top: 0; transform: rotate(-45deg); transition: transform 0.2s; }
.caret.open::before { transform: rotate(45deg); }
.caret.placeholder { opacity: 0; pointer-events: none; }

/* Label */
.label { all: unset; cursor: pointer; color: #334155; font-size: 13.5px; font-weight: 500; line-height: 1.3; padding: 2px 0; display: inline-flex; align-items: center; gap: 4px; transition: color 0.15s; }
.label:hover { color: #4f46e5; }

/* Children */
.children { position: relative; display: flex; flex-direction: column; gap: 10px; padding-left: 32px; min-height: 36px; }
.children::before { content: ''; position: absolute; left: 0; top: -18px; bottom: 10px; border-left: 1.5px solid #cbd5e1; }
.children > .node { position: relative; min-height: 36px; }
.children > .node::before { content: ''; position: absolute; left: -32px; top: 50%; width: 32px; border-top: 1.5px solid #cbd5e1; transform: translateY(-50%); z-index: 1; }
.node:not(.is-root)::after { content: ''; position: absolute; left: -16px; top: 50%; width: 16px; border-top: 1.5px solid #cbd5e1; transform: translateY(-50%); z-index: 2; }
</style>
